import {
    doc,
    getDoc,
    setDoc,
    serverTimestamp,
    increment,
    collection,
    query,
    where,
    getCountFromServer,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FeatureKey, FeatureKeys } from '../models/featureKeys';
import { SubscriptionTier, TIER_LEVELS, TierDefinitionsDoc, FeatureDefinition } from '../models/index';
import { User, getEffectiveEmail } from '../models/index';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FeatureCheckResult {
    /** Whether the user's tier grants access. */
    hasAccess: boolean;
    /** Whether the user can use the feature right now (tier + counter). */
    canUse: boolean;
    /** Remaining uses (-1 = unlimited, 0 = exhausted). */
    remaining: number;
    /** Tier-specific limit (-1 = unlimited). */
    limit: number;
    /** Minimum tier name required, e.g. 'Pro'. */
    requiredTier: string;
}

export class FeatureAccessError extends Error {
    featureKey: string;
    isLocked: boolean; // true = tier gate; false = counter exceeded
    requiredTier?: string;

    constructor(featureKey: string, isLocked: boolean, requiredTier?: string) {
        super(
            isLocked
                ? `Feature "${featureKey}" requires ${requiredTier ?? 'a higher tier'}.`
                : `Usage limit reached for "${featureKey}".`
        );
        this.featureKey = featureKey;
        this.isLocked = isLocked;
        this.requiredTier = requiredTier;
    }
}

// ── Internal cache ────────────────────────────────────────────────────────────

let _tierDefs: TierDefinitionsDoc | null = null;
let _tierDefsLoadedAt: number | null = null;
const CACHE_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

// Per-period usage counter cache: Map<`${userId}/${period}`, Record<field, number>>
const _counterCache = new Map<string, Record<string, number>>();

// ── Helpers ───────────────────────────────────────────────────────────────────

async function loadTierDefinitions(force = false): Promise<TierDefinitionsDoc | null> {
    const now = Date.now();
    if (!force && _tierDefs && _tierDefsLoadedAt && now - _tierDefsLoadedAt < CACHE_MAX_AGE_MS) {
        return _tierDefs;
    }
    try {
        const snap = await getDoc(doc(db, 'app_config', 'tier_definitions'));
        if (!snap.exists()) return null;
        const data = snap.data() as TierDefinitionsDoc;
        _tierDefs = data;
        _tierDefsLoadedAt = now;
        return data;
    } catch (e) {
        console.error('[FeatureFlagService] Error loading tier_definitions:', e);
        return null;
    }
}

function tierDisplayName(tierId: SubscriptionTier): string {
    const names: Record<SubscriptionTier, string> = {
        [SubscriptionTier.Free]: 'Free',
        [SubscriptionTier.Pro]: 'Pro',
        [SubscriptionTier.Premium]: 'Premium',
        [SubscriptionTier.Family]: 'Family',
    };
    return names[tierId] ?? tierId;
}

function requiredTierName(def: FeatureDefinition): string {
    const level = def.min_tier;
    const found = Object.entries(TIER_LEVELS).find(([, l]) => l === level);
    if (!found) return level < 0 ? 'Admin' : 'Pro';
    return tierDisplayName(found[0] as SubscriptionTier);
}

function periodKey(period?: string): string {
    const now = new Date();
    const pad = (n: number, w = 2) => String(n).padStart(w, '0');
    if (period === 'daily') {
        return `daily_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    }
    if (period === 'monthly') {
        return `monthly_${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
    }
    return 'total';
}

/** Maps a feature key to the Firestore counter field name. */
function counterField(featureKey: FeatureKey): string {
    const mapping: Partial<Record<FeatureKey, string>> = {
        [FeatureKeys.geminiAiQuery]: 'ai_queries',
        [FeatureKeys.geminiParseEmail]: 'ai_queries',
        [FeatureKeys.aiTripImprovement]: 'ai_queries',
        [FeatureKeys.createTripManual]: 'trips_created',
        [FeatureKeys.createTripAi]: 'trips_created',
        [FeatureKeys.googlePlacesSearch]: 'places_searches',
        [FeatureKeys.aviationLookup]: 'aviation_lookups',
        [FeatureKeys.aviationRouteSearch]: 'aviation_lookups',
        [FeatureKeys.generateBlogPost]: 'blog_posts_generated',
        [FeatureKeys.writeBlogPost]: 'blog_posts_generated',
        [FeatureKeys.documentOcr]: 'ocr_scans',
        [FeatureKeys.addActivity]: 'activities_added',
        [FeatureKeys.addAccommodation]: 'accommodations_added',
        [FeatureKeys.addFlight]: 'flights_added',
        [FeatureKeys.addCarRental]: 'car_rentals_added',
        [FeatureKeys.addRegion]: 'regions_added',
        [FeatureKeys.addExpense]: 'expenses_added',
        [FeatureKeys.addParticipant]: 'participants_added',
        [FeatureKeys.addTripDocument]: 'trip_documents_added',
        [FeatureKeys.addTravelDocument]: 'trip_documents_added',
        [FeatureKeys.createEquipmentList]: 'equipment_lists_created',
        [FeatureKeys.importTripJson]: 'json_imports',
    };
    return mapping[featureKey] ?? featureKey.replace(/\//g, '_');
}

async function getUsed(userId: string, featureKey: FeatureKey, def: FeatureDefinition, tripId?: string): Promise<number> {
    // Trip creation limits: count actual trips owned by the user (authoritative)
    // The usage counter only increments and never decrements on deletion,
    // so counting real trips avoids blocking users who deleted trips.
    if (counterField(featureKey) === 'trips_created') {
        try {
            const tripsQuery = query(
                collection(db, 'trips'),
                where('ownerId', '==', userId)
            );
            const snapshot = await getCountFromServer(tripsQuery);
            return snapshot.data().count;
        } catch (e) {
            console.error('[FeatureFlagService] Error counting trips for limit check:', e);
            // Fall through to counter-based reading on error
        }
    }

    // Per-trip: counters live on the trip document, not under the user.
    if (def.limit_period === 'per_trip') {
        if (!tripId) return 0;
        const cacheKey = `trip_${tripId}`;
        let cached = _counterCache.get(cacheKey);
        if (!cached) {
            try {
                const snap = await getDoc(
                    doc(collection(db, 'trips', tripId, 'usage_counters'), 'trip_content')
                );
                cached = snap.exists() ? (snap.data() as Record<string, number>) : {};
                _counterCache.set(cacheKey, cached);
            } catch (e) {
                console.error('[FeatureFlagService] Error loading trip counters:', e);
                return 0;
            }
        }
        return cached[counterField(featureKey)] ?? 0;
    }

    const pk = periodKey(def.limit_period);
    const cacheKey = `${userId}/${pk}`;
    let cached = _counterCache.get(cacheKey);
    if (!cached) {
        try {
            const snap = await getDoc(
                doc(collection(db, 'users', userId, 'usage_counters'), pk)
            );
            cached = snap.exists() ? (snap.data() as Record<string, number>) : {};
            _counterCache.set(cacheKey, cached);
        } catch (e) {
            console.error('[FeatureFlagService] Error loading counters:', e);
            return 0;
        }
    }
    return cached[counterField(featureKey)] ?? 0;
}

/** Invalidate the counter cache for a user (call after consumeUsage). */
function invalidateCounterCache(userId: string) {
    Array.from(_counterCache.keys()).forEach((key) => {
        if (key.startsWith(`${userId}/`)) _counterCache.delete(key);
    });
}

// ── Sysadmin bypass ──────────────────────────────────────────────────────────

const SYSADMIN_EMAIL = 'erez.zohar@gmail.com';

/** Returns true for the system administrator — bypasses all tier/counter gates. */
function isSysAdminUser(user: User | null | undefined): boolean {
    return !!user && getEffectiveEmail(user) === SYSADMIN_EMAIL;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns `true` when [user]'s tier grants access to [featureKey].
 * Respects per-user featureOverrides.
 */
export function hasFeature(user: User | null | undefined, featureKey: FeatureKey): boolean {
    if (!user) return false;
    if (isSysAdminUser(user)) return true; // sysadmin has access to everything
    if (user.isBlocked) return false;

    // Per-user admin override wins
    const override = user.featureOverrides?.[featureKey];
    if (override !== undefined) return override;

    if (!_tierDefs) return true; // defs not yet loaded — optimistic

    const def = _tierDefs.features[featureKey];
    if (!def) return true; // unknown key — allow

    if (def.min_tier < 0) return user.isAdmin; // admin only
    if (TIER_LEVELS[user.tier] < def.min_tier) return false;

    // Family-excluded features: deny Family-tier users even though their
    // numeric level (3) is higher than Premium's (2).
    if (def.family_excluded && user.tier === SubscriptionTier.Family) return false;

    return true;
}

/**
 * Fail-CLOSED gate for the Premium-exclusive clone-share ("save their own copy")
 * capability.
 *
 * `hasFeature` fails OPEN — it returns true when the tier definitions haven't
 * loaded yet (`!_tierDefs`) or the key is missing from the live
 * `app_config/tier_definitions` doc (`!def`). For a paid gate that's unsafe: it
 * let Free users offer cloning. So resolve entitlement directly from the user's
 * real tier instead of trusting that default. Mirrors the config policy for
 * `share_template` (min_tier = Premium, family-excluded) and the mobile
 * `_resolveCanOfferClone`.
 */
export function canOfferCloneShare(user: User | null | undefined): boolean {
    if (!user) return false;
    if (isSysAdminUser(user)) return true;
    if (user.isBlocked) return false;

    // Explicit per-user override wins both ways.
    const override = user.featureOverrides?.[FeatureKeys.shareTemplate];
    if (override !== undefined) return override;

    if (user.isAdmin) return true; // admins / sysadmin

    // Premium-only (Family is excluded for this feature).
    return user.tier === SubscriptionTier.Premium;
}

/**
 * Returns `true` when the user can use [featureKey] right now
 * (tier access + counter not exceeded).
 *
 * [tripId] is required for features with `limit_period == 'per_trip'`.
 */
export async function canUse(
    user: User | null | undefined,
    featureKey: FeatureKey,
    tripId?: string
): Promise<boolean> {
    if (!user) return false;
    if (isSysAdminUser(user)) return true; // sysadmin: always allowed, no counter check
    if (!hasFeature(user, featureKey)) return false;
    const defs = await loadTierDefinitions();
    if (!defs) return true;

    const def = defs.features[featureKey];
    if (!def?.limits) return true;

    const limit = def.limits
        ? (def.limits as Record<string, number>)[user!.tier] ?? -1
        : -1;
    if (limit < 0) return true;

    const used = await getUsed(user!.id, featureKey, def, tripId);
    return used < limit;
}

/**
 * Returns remaining uses for [featureKey] for the given user.
 * Returns -1 for unlimited, 0 when exhausted.
 *
 * [tripId] is required for features with `limit_period == 'per_trip'`.
 */
export async function getRemainingUsage(
    user: User | null | undefined,
    featureKey: FeatureKey,
    tripId?: string
): Promise<number> {
    if (!user) return 0;
    if (isSysAdminUser(user)) return -1; // sysadmin: unlimited
    if (!hasFeature(user, featureKey)) return 0;
    const defs = await loadTierDefinitions();
    if (!defs) return -1;

    const def = defs.features[featureKey];
    if (!def?.limits) return -1;

    const limit = (def.limits as Record<string, number>)[user!.tier] ?? -1;
    if (limit < 0) return -1;

    const used = await getUsed(user!.id, featureKey, def, tripId);
    return Math.max(0, limit - used);
}

/** Returns the tier-specific usage limit for [featureKey]. -1 = unlimited. */
export function getLimit(user: User | null | undefined, featureKey: FeatureKey): number {
    if (!user || !_tierDefs) return -1;
    if (isSysAdminUser(user)) return -1; // sysadmin: unlimited
    const def = _tierDefs.features[featureKey];
    if (!def?.limits) return -1;
    return (def.limits as Record<string, number>)[user.tier] ?? -1;
}

/**
 * Increments the usage counter for [featureKey] in Firestore.
 * Returns `true` on success, `false` if limit was already reached.
 *
 * [tripId] is required for features with `limit_period == 'per_trip'`.
 */
export async function consumeUsage(
    user: User | null | undefined,
    featureKey: FeatureKey,
    throwOnLimitReached = false,
    tripId?: string
): Promise<boolean> {
    if (!user) return false;
    if (isSysAdminUser(user)) return true; // sysadmin: allow without incrementing counters
    const allowed = await canUse(user, featureKey, tripId);
    if (!allowed) {
        if (throwOnLimitReached) {
            const def = _tierDefs?.features[featureKey];
            const isLocked = !hasFeature(user, featureKey);
            throw new FeatureAccessError(
                featureKey,
                isLocked,
                def ? requiredTierName(def) : undefined
            );
        }
        return false;
    }

    const defs = await loadTierDefinitions();
    if (!defs) return true; // can't counter — allow

    const def = defs.features[featureKey];
    if (!def?.limits) return true; // boolean gate, nothing to increment

    const field = counterField(featureKey);
    try {
        if (def.limit_period === 'per_trip' && tripId) {
            // Per-trip: write to trips/{tripId}/usage_counters/trip_content
            const cacheKey = `trip_${tripId}`;
            await setDoc(
                doc(collection(db, 'trips', tripId, 'usage_counters'), 'trip_content'),
                { [field]: increment(1), updated_at: serverTimestamp() },
                { merge: true }
            );
            _counterCache.delete(cacheKey);
        } else {
            const pk = periodKey(def.limit_period);
            await setDoc(
                doc(collection(db, 'users', user.id, 'usage_counters'), pk),
                { [field]: increment(1), updated_at: serverTimestamp() },
                { merge: true }
            );
            invalidateCounterCache(user.id);
        }
        return true;
    } catch (e) {
        console.error('[FeatureFlagService] consumeUsage error:', e);
        return false;
    }
}

/** Returns the display name of the minimum tier required for [featureKey]. */
export function getRequiredTier(featureKey: FeatureKey): string {
    if (!_tierDefs) return 'Pro';
    const def = _tierDefs.features[featureKey];
    if (!def) return 'Free';
    return requiredTierName(def);
}

/**
 * Returns the display name of the lowest tier above the user's current tier
 * that provides a higher (or unlimited) limit for [featureKey].
 *
 * Use this when a user has hit their quota — the upgrade target is not the
 * minimum *access* tier but the next tier with more quota.
 * Falls back to 'Pro' if undetermined.
 */
export function getUpgradeTierForLimit(featureKey: FeatureKey, userTier: string): string {
    if (!_tierDefs) return 'Pro';
    const def = _tierDefs.features[featureKey];
    if (!def?.limits) return 'Pro';

    const userLevel = TIER_LEVELS[userTier as SubscriptionTier] ?? 0;
    const currentLimit = (def.limits as Record<string, number>)[userTier] ?? -1;

    // Walk tiers in ascending level order, find the first one above the user's
    // tier that grants a higher (or unlimited) quota.
    const tiersAbove = (Object.entries(TIER_LEVELS) as [SubscriptionTier, number][])
        .filter(([, level]) => level > userLevel)
        .sort(([, a], [, b]) => a - b);

    for (const [tier] of tiersAbove) {
        const tierLimit = (def.limits as Record<string, number>)[tier] ?? -1;
        if (tierLimit < 0 || (currentLimit >= 0 && tierLimit > currentLimit)) {
            return tierDisplayName(tier);
        }
    }

    return 'Pro'; // fallback
}

/** Force-refresh the cached tier definitions. */
export async function refreshTierDefinitions(): Promise<void> {
    await loadTierDefinitions(true);
}

/** Pre-load tier definitions (call once after app initialises). */
export async function initialiseTierDefinitions(): Promise<void> {
    await loadTierDefinitions();
}

/**
 * Returns a full [FeatureCheckResult] for [featureKey] in one call.
 * Useful when a component needs all info at once.
 *
 * [tripId] is required for features with `limit_period == 'per_trip'`.
 */
export async function checkFeature(
    user: User | null | undefined,
    featureKey: FeatureKey,
    tripId?: string
): Promise<FeatureCheckResult> {
    if (isSysAdminUser(user)) {
        return { hasAccess: true, canUse: true, remaining: -1, limit: -1, requiredTier: 'Free' };
    }
    const defs = await loadTierDefinitions();
    const def = defs?.features[featureKey];
    const access = hasFeature(user, featureKey);
    const limit = getLimit(user, featureKey);
    const remaining = access ? await getRemainingUsage(user, featureKey, tripId) : 0;
    const usable = access && (remaining === -1 || remaining > 0);

    return {
        hasAccess: access,
        canUse: usable,
        remaining,
        limit,
        requiredTier: def ? requiredTierName(def) : 'Pro',
    };
}

// ── Test injection helpers (@visibleForTesting) ───────────────────────────────
// These must NOT be used in production code.

export const __test = {
    /** Inject tier definitions and optionally set the loaded-at timestamp. */
    setTierDefs(
        defs: TierDefinitionsDoc | null,
        loadedAt: number | null = defs ? Date.now() : null,
    ) {
        _tierDefs = defs;
        _tierDefsLoadedAt = loadedAt;
    },

    /** Pre-populate a counter cache entry (avoids Firestore reads in tests). */
    setCounterCache(key: string, data: Record<string, number>) {
        _counterCache.set(key, data);
    },

    /** Clear all counter cache entries. */
    clearCounterCache() {
        _counterCache.clear();
    },

    /** Overwrite the tier-defs loaded-at timestamp (to simulate cache expiry). */
    setTierDefsLoadedAt(ts: number | null) {
        _tierDefsLoadedAt = ts;
    },

    /** Reset all module-level state.  Call in beforeEach. */
    reset() {
        _tierDefs = null;
        _tierDefsLoadedAt = null;
        _counterCache.clear();
    },
};

